import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gbp = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

const BookingSuccess = () => {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id") || params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | failed
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!bookingId) return;
    try {
      const { data } = await axios.get(`${API}/bookings/status/${bookingId}`);
      setBooking(data);
      if (data.payment_status === "paid") {
        setStatus("paid");
        return;
      }
      if (data.payment_status === "expired") {
        setStatus("expired");
        return;
      }
      if (attempts >= 8) {
        setStatus("timeout");
        return;
      }
      setAttempts((a) => a + 1);
      setTimeout(poll, 2000);
    } catch (err) {
      setStatus("failed");
    }
  }, [bookingId, attempts]);

  useEffect(() => {
    if (!bookingId) {
      setStatus("failed");
      return;
    }
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center p-6">
      <div
        data-testid="booking-success-page"
        className="max-w-xl w-full bg-white border border-[#DDD1BE] rounded-2xl shadow-sm p-10 md:p-12"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#6A5F58] mb-8 link-underline"
          data-testid="success-back-home"
        >
          <ArrowLeft size={14} /> Back to Walnut Lakes
        </Link>

        {status === "polling" && (
          <div className="text-center">
            <Loader2 size={40} className="mx-auto text-[#0F0E0E] animate-spin" strokeWidth={1.3} />
            <h1 className="font-serif text-3xl md:text-4xl mt-6 text-[#14100F]">
              Confirming your payment…
            </h1>
            <p className="mt-4 text-[#6A5F58]">This usually takes a few seconds.</p>
          </div>
        )}

        {status === "paid" && booking && (
          <div data-testid="success-paid">
            <CheckCircle2 size={44} className="text-[#0F0E0E]" strokeWidth={1.3} />
            <h1 className="font-serif text-4xl md:text-5xl mt-6 leading-[1.05] text-[#14100F]">
              Your dates are held.
            </h1>
            <p className="mt-5 text-[#3A322E] leading-relaxed">
              Thank you — your deposit of{" "}
              <strong>{gbp(booking.deposit)}</strong> has been received. We&rsquo;ll
              email you a confirmation shortly. The balance of{" "}
              <strong>{gbp(booking.total - booking.deposit)}</strong> is due on arrival.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
              <div className="border-t border-[#DDD1BE] pt-3">
                <div className="overline text-[#6A5F58]">Arrival</div>
                <div className="mt-1 text-[#14100F]">{booking.start_date}</div>
              </div>
              <div className="border-t border-[#DDD1BE] pt-3">
                <div className="overline text-[#6A5F58]">Departure</div>
                <div className="mt-1 text-[#14100F]">{booking.end_date}</div>
              </div>
              <div className="border-t border-[#DDD1BE] pt-3">
                <div className="overline text-[#6A5F58]">Unit</div>
                <div className="mt-1 text-[#14100F]">{booking.unit_label || booking.unit_id}</div>
              </div>
              <div className="border-t border-[#DDD1BE] pt-3">
                <div className="overline text-[#6A5F58]">Reference</div>
                <div className="mt-1 text-[#14100F] break-all">{booking.booking_id.slice(0, 8)}</div>
              </div>
            </div>
          </div>
        )}

        {(status === "expired" || status === "failed" || status === "timeout") && (
          <div data-testid="success-failed">
            <XCircle size={44} className="text-[#8C2E39]" strokeWidth={1.3} />
            <h1 className="font-serif text-4xl mt-6 text-[#14100F]">
              {status === "expired" ? "This session expired." : "We couldn't confirm your payment."}
            </h1>
            <p className="mt-5 text-[#3A322E] leading-relaxed">
              No deposit has been taken. Try again — or drop us a note and we&rsquo;ll sort it out.
            </p>
            <Link to="/#book" className="btn-primary mt-8 inline-flex" data-testid="success-retry">
              Try again
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingSuccess;
